import { z } from 'zod';

/**
 * What a song audit entry records.
 *
 * `entity` is fixed to `song` today. It exists so a future role or station audit
 * feed can join the same endpoint shape without a breaking change on the client.
 */
export const SongAuditActionSchema = z.enum([
  'create',
  'update',
  'delete',
  'reassign',
  'cover-art.set',
  'cover-art.clear',
  'tag.bulk-apply',
  'tag.merge',
  'trim',
  'trim.reset',
  'restore',
]);
export type SongAuditAction = z.infer<typeof SongAuditActionSchema>;

export const AuditSourceSchema = z.enum(['admin-ui', 'worker', 'script', 'system']);
export type AuditSource = z.infer<typeof AuditSourceSchema>;

export const SongAuditEntrySchema = z.object({
  entity: z.literal('song'),
  id: z.string(),
  /** Null once the song has been deleted and its id no longer resolves. */
  songId: z.string().nullable(),
  /** Title at the time of the action, so deleted songs stay identifiable. */
  songTitle: z.string(),
  actorUid: z.string().nullable(),
  actorDisplayName: z.string().nullable(),
  action: SongAuditActionSchema,
  source: AuditSourceSchema,
  changedFields: z.array(z.string()),
  before: z.record(z.unknown()).nullable(),
  after: z.record(z.unknown()).nullable(),
  requestId: z.string().nullable(),
  createdAt: z.coerce.date(),
  /**
   * Number of songs affected by the operation this entry belongs to.
   *
   * 1 for an ordinary edit. Greater than 1 on a collapsed bulk row, where the feed
   * folds every per-song row from one tag merge or bulk delete into a single entry.
   */
  affectedCount: z.number().int().min(1),
});
export type SongAuditEntry = z.infer<typeof SongAuditEntrySchema>;

export const SongAuditFiltersSchema = z.object({
  songId: z.string().optional(),
  actorUid: z.string().optional(),
  action: SongAuditActionSchema.optional(),
  /** ISO timestamp; entries at or after this instant. */
  since: z.coerce.date().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type SongAuditFilters = z.infer<typeof SongAuditFiltersSchema>;

export const SongAuditListResponseSchema = z.object({
  items: z.array(SongAuditEntrySchema),
  total: z.number().int().min(0),
  limit: z.number().int(),
  offset: z.number().int(),
});
export type SongAuditListResponse = z.infer<typeof SongAuditListResponseSchema>;
